import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { getActiveOrderHistory } from '@kfc-global/react-shared/redux/Actions/OrderHistoryAction';
import { GET_ACTIVE_ORDER_HISTORY_MAX_RETRY_INTERVAL_MS } from '@kfc-global/react-shared/config/config.constants';
import { config } from '@kfc-global/react-shared/config/config.utils';

import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import { ROUTE_URL, DATE_FORMATS, SESSION_STORAGE_KEYS } from 'common/constants/SharedConstants';
import { getDisplayOrderId } from 'common/utilities/orderDetailsUtils';
import { updateOrderTrackingDetailsInLocalStorage } from 'common/utilities/orderProcessingSummaryUtils';
import {
  isSupportedTenantCode,
  isDeliveryDisposition,
  isPickupDisposition,
  redirectToActiveOrdersPage,
  isCateringDisposition,
} from 'common/utilities/utils';
import { isOrderCompletedOnFulfillmentDateTime } from 'common/utilities/orderStatusUtils';
import { getFormattedFromUnixWithTimeZone } from 'common/utilities/dayJsUtils';

/*********************************************************************
 * getINHeaderTrackOrderTitle
 *
 *********************************************************************/
export const getHeaderTrackOrderTitleIN = postActiveOrderHistoryDetailsList => {
  const postActiveOrderDetail = postActiveOrderHistoryDetailsList[0];

  if (postActiveOrderHistoryDetailsList?.length === 1) {
    if (postActiveOrderDetail?.requestedDateTime?.isForNow === false) {
      const returnTxt = `${translateWithI18Next(TRANSLATE_MAPPING_KEY?.YOUR_ORDER)} # ${getDisplayOrderId(
        postActiveOrderDetail,
      )} ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.HAS_SCHEDULED)} `;

      if (isDeliveryDisposition(postActiveOrderDetail?.service)) {
        return `${returnTxt} ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.DELIVERY)} ${translateWithI18Next(
          TRANSLATE_MAPPING_KEY?.AT,
        )}`;
      } else if (isPickupDisposition(postActiveOrderDetail?.service)) {
        return `${returnTxt} ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.PICKUP)} ${translateWithI18Next(
          TRANSLATE_MAPPING_KEY?.AT,
        )}`;
      } else {
        return `${returnTxt}`;
      }
    } else if (isDeliveryDisposition(postActiveOrderDetail?.service)) {
      return `${translateWithI18Next(TRANSLATE_MAPPING_KEY?.YOUR_ORDER)} # ${getDisplayOrderId(
        postActiveOrderDetail,
      )} ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.HAS_BEEN_CONFIRMED)}`;
    } else if (isPickupDisposition(postActiveOrderDetail?.service)) {
      return `${translateWithI18Next(TRANSLATE_MAPPING_KEY?.WE_ARE_WORKING_ON)} ${
        postActiveOrderHistoryDetailsList?.length
      } ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDERS_NOW)}`;
    }
  } else {
    return `${translateWithI18Next(TRANSLATE_MAPPING_KEY?.WE_ARE_WORKING_ON)} ${
      postActiveOrderHistoryDetailsList?.length
    } ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDERS_NOW)}`;
  }
};

/*********************************************************************
 * getHeaderTrackOrderTitleAU
 *
 *********************************************************************/
const getHeaderTrackOrderTitleAU = postActiveOrderHistoryDetailsList => {
  const postActiveOrderDetail = postActiveOrderHistoryDetailsList[0];
  const time =
    postActiveOrderDetail?.deliveryDetails?.estimatedDeliveryTime || postActiveOrderDetail?.fulfillmentDateTime;

  if (postActiveOrderHistoryDetailsList?.length === 1) {
    if (
      (window?.location?.pathname === ROUTE_URL?.HOME ||
        window?.location?.pathname === ROUTE_URL?.HOME_PAGE ||
        window?.location?.pathname === ROUTE_URL?.ACCOUNT) &&
      isCateringDisposition(postActiveOrderDetail?.service)
    ) {
      return `${translateWithI18Next(TRANSLATE_MAPPING_KEY?.YOUR_ORDER)} # ${getDisplayOrderId(
        postActiveOrderDetail,
      )} ${translateWithI18Next(
        TRANSLATE_MAPPING_KEY?.IS_SCHEDULED_TO_BE_PICKED_UP_AT,
      )} ${getFormattedFromUnixWithTimeZone(
        postActiveOrderDetail.fulfillmentDateTime,
        postActiveOrderDetail?.store?.timeZoneIANA,
        DATE_FORMATS?.H_MM_A,
      )}`;
    } else if (
      (window?.location?.pathname === ROUTE_URL?.HOME ||
        window?.location?.pathname === ROUTE_URL?.HOME_PAGE ||
        window?.location?.pathname === ROUTE_URL?.ACCOUNT) &&
      isDeliveryDisposition(postActiveOrderDetail?.service)
    ) {
      return (
        `${translateWithI18Next(TRANSLATE_MAPPING_KEY?.YOUR_ORDER)} # ${getDisplayOrderId(
          postActiveOrderDetail,
        )} ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.DELIVERED_AROUND)} ` +
        getFormattedFromUnixWithTimeZone(time, postActiveOrderDetail?.store?.timeZoneIANA, DATE_FORMATS?.H_MM_A)
      );
    } else {
      return (
        <span>
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.WE_ARE_WORKING_ON) + ' '}
          <b className='your-order'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.YOUR_ORDER)?.toLowerCase()}</b>
          {' ' + translateWithI18Next(TRANSLATE_MAPPING_KEY?.NOW)}
        </span>
      );
    }
  } else {
    return (
      <span>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.WE_ARE_WORKING_ON)}
        <b className='your-order'>
          {' '}
          {postActiveOrderHistoryDetailsList?.length + ' '}
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDERS) + ' '}
        </b>
        {translateWithI18Next(TRANSLATE_MAPPING_KEY?.NOW)}
      </span>
    );
  }
};

/*********************************************************************
 * getHeaderTrackOrderTitle
 *
 *********************************************************************/
export const getHeaderTrackOrderTitle = params => {
  const { headerTrackDetails } = params;
  const { postActiveOrderHistoryDetailsList, enableGlobalOrderTracking } = headerTrackDetails;
  postActiveOrderHistoryDetailsList[0].enableGlobalOrderTracking = enableGlobalOrderTracking;
  if (isSupportedTenantCode()) {
    return getHeaderTrackOrderTitleAU(postActiveOrderHistoryDetailsList);
  }
  return getHeaderTrackOrderTitleIN(postActiveOrderHistoryDetailsList);
};

/*********************************************************************
 * getHeaderTrackOrderButtonLabel
 *
 *********************************************************************/
export const getHeaderTrackOrderButtonLabel = params => {
  return translateWithI18Next(TRANSLATE_MAPPING_KEY?.TRACK_ORDER);
};

/*********************************************************************
 * recursiveDispatchGetActiveOrderHistory
 *
 *********************************************************************/
export const recursiveDispatchGetActiveOrderHistory = params => {
  const { headerTrackDetails, updateSetHeaderTrackDetails } = params;
  const { dispatchGetActiveOrderHistoryTimer } = headerTrackDetails;

  if (!dispatchGetActiveOrderHistoryTimer) {
    const dispatchGetActiveOrderHistoryTimer = setTimeout(() => {
      clearDispatchGetActiveOrderHistoryTimer(params);
      dispatchGetActiveOrderHistory(params);
      updateSetHeaderTrackDetails({ dispatchGetActiveOrderHistoryTimer });
    }, config(GET_ACTIVE_ORDER_HISTORY_MAX_RETRY_INTERVAL_MS));
  }
};

/*********************************************************************
 * dispatchGetActiveOrderHistory
 *
 *********************************************************************/
export const dispatchGetActiveOrderHistory = params => {
  const { headerTrackDetails } = params;
  const { customerId, tenantId, props, dispatch } = headerTrackDetails;

  if (customerId && tenantId && props?.isHeaderTrackOrderEnabled()) {
    const postBody = {
      OrderStatus: '',
      OrderMode: '',
    };
    dispatch(
      getActiveOrderHistory(
        {
          customerId,
          tenantId,
        },
        postBody,
      ),
    );
  }
};

/*********************************************************************
 * clearDispatchGetActiveOrderHistoryTimer
 *
 *********************************************************************/
export const clearDispatchGetActiveOrderHistoryTimer = params => {
  const { headerTrackDetails, updateSetHeaderTrackDetails } = params;
  const { dispatchGetActiveOrderHistoryTimer } = headerTrackDetails;

  if (dispatchGetActiveOrderHistoryTimer) {
    clearTimeout(dispatchGetActiveOrderHistoryTimer);
    updateSetHeaderTrackDetails({ dispatchGetActiveOrderHistoryTimer: undefined });
  }
};

/*********************************************************************
 * updateHeaderTrackDetailsWithOrderHistoryReducer
 *
 *********************************************************************/
export const updateHeaderTrackDetailsWithOrderHistoryReducer = params => {
  const { headerTrackDetails, updateSetHeaderTrackDetails } = params;
  const { postActiveOrderHistoryDetailsList, isActiveOrderAvailable } = headerTrackDetails;

  if (postActiveOrderHistoryDetailsList?.length > 0) {
    if (postActiveOrderHistoryDetailsList?.length) {
      updateSetHeaderTrackDetails({
        isActiveOrderAvailable: true,
        trackOrderTitle: getHeaderTrackOrderTitle(params),
        trackOrderButtonLabel: getHeaderTrackOrderButtonLabel(params),
      });
      recursiveDispatchGetActiveOrderHistory({ headerTrackDetails, updateSetHeaderTrackDetails });
    } else {
      isActiveOrderAvailable &&
        updateSetHeaderTrackDetails({
          isActiveOrderAvailable: false,
        });
    }
  }
};

/*********************************************************************
 * singleOrderTracker
 *
 *********************************************************************/
export const singleOrderTracker = params => {
  const { headerTrackDetails, redirectTo } = params;
  const { postActiveOrderHistoryDetailsList, useHistoryHook } = headerTrackDetails;

  postActiveOrderHistoryDetailsList?.length > 0 &&
    updateOrderTrackingDetailsInLocalStorage({
      trackDetails: headerTrackDetails,
      orderDetails: postActiveOrderHistoryDetailsList[0],
      redirectTo,
      props: useHistoryHook,
    });
};

/*********************************************************************
 * onTrackOrderBtnClickHandler
 *
 *********************************************************************/
export const onTrackOrderBtnClickHandler = params => {
  const { headerTrackDetails } = params;
  const { postActiveOrderHistoryDetailsList, useHistoryHook } = headerTrackDetails;

  if (postActiveOrderHistoryDetailsList && postActiveOrderHistoryDetailsList?.length > 0) {
    if (postActiveOrderHistoryDetailsList?.length === 1) {
      singleOrderTracker(params);
    } else {
      redirectToActiveOrdersPage(useHistoryHook);
    }
  }
};

/*********************************************************************
 * validateAndGetPostActiveOrderHistoryDetailsList
 *
 *********************************************************************/
export const validateAndGetPostActiveOrderHistoryDetailsList = params => {
  return params?.filter(item => !isOrderCompletedOnFulfillmentDateTime(item));
};

/*********************************************************************
 * updatePickupOrderStateValue
 *
 *********************************************************************/
export const updatePickupOrderStateValue = params => {
  const { headerTrackDetails, location, updateSetHeaderTrackDetails, props } = params;

  if (
    headerTrackDetails?.showPickupOrderCancelledBanner &&
    location?.pathname !== ROUTE_URL.HOME &&
    location?.pathname !== ROUTE_URL.HOME_PAGE &&
    (headerTrackDetails?.isActiveOrderAvailable === false || headerTrackDetails?.userStatus?.loggedIn === false)
  ) {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.PICKUP_ORDER_CANCELLED);
    updateSetHeaderTrackDetails({ showPickupOrderCancelledBanner: false });
    props.setShowBlackStripeContent(true);
  }
};
